import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SeoHead from '../../components/feature/SeoHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const servicesSchema = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Doula Services by Lotus Maternal — Brooklyn, NY',
  description: 'Comprehensive doula services for all families including pregnancy support, birth coaching, and postpartum care in Brooklyn, NY.',
  url: `${siteUrl}/services`,
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/services` },
    ],
  },
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      item: {
        '@type': 'Service',
        name: 'Pregnancy Support',
        description: 'Comprehensive prenatal care, education, and emotional guidance from the moment you discover you\'re expecting.',
        provider: { '@type': 'MedicalBusiness', name: 'Lotus Maternal', url: siteUrl },
        areaServed: { '@type': 'City', name: 'Brooklyn', containedInPlace: { '@type': 'State', name: 'New York' } },
      },
    },
    {
      '@type': 'ListItem',
      position: 2,
      item: {
        '@type': 'Service',
        name: 'Birth Support',
        description: 'Continuous labor and delivery support ensuring you feel safe, informed, and empowered throughout your birth experience.',
        provider: { '@type': 'MedicalBusiness', name: 'Lotus Maternal', url: siteUrl },
        areaServed: { '@type': 'City', name: 'Brooklyn', containedInPlace: { '@type': 'State', name: 'New York' } },
      },
    },
    {
      '@type': 'ListItem',
      position: 3,
      item: {
        '@type': 'Service',
        name: 'Postpartum Support',
        description: 'Comprehensive postpartum care including home visits, lactation support, newborn care education, and emotional support.',
        provider: { '@type': 'MedicalBusiness', name: 'Lotus Maternal', url: siteUrl },
        areaServed: { '@type': 'City', name: 'Brooklyn', containedInPlace: { '@type': 'State', name: 'New York' } },
      },
    },
    {
      '@type': 'ListItem',
      position: 4,
      item: {
        '@type': 'Service',
        name: 'Virtual Doula Services',
        description: 'Comprehensive virtual doula support via Zoom for clients nationwide — prenatal, birth, and postpartum.',
        provider: { '@type': 'MedicalBusiness', name: 'Lotus Maternal', url: siteUrl },
        areaServed: { '@type': 'Country', name: 'United States' },
      },
    },
  ],
};

const ServicesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Doula Services Brooklyn NY — Pregnancy, Birth & Postpartum | Lotus Maternal"
        description="Explore Lotus Maternal's full range of doula services in Brooklyn, NY: prenatal care, birth coaching, postpartum support, and virtual services. Compassionate, personalized care for all families."
        keywords="pregnancy doula Brooklyn NY, birth support services, postpartum care New York, prenatal visits, virtual doula, maternal health Brooklyn"
        canonical={`${siteUrl}/services`}
        ogTitle="Doula Services — Pregnancy, Birth & Postpartum | Lotus Maternal"
        ogDescription="Comprehensive doula services for all families in Brooklyn, NY. Prenatal care, birth coaching, postpartum support, and virtual services."
        schema={servicesSchema}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/9076b2f5a1e6143261a30afef69eb6dc.jpg"
            alt="Our Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-6 space-y-4">
          <h1 className="font-playfair text-5xl lg:text-7xl font-bold">Our Services</h1>
          <p className="text-xl lg:text-2xl max-w-3xl mx-auto">
            Comprehensive support for every stage of your maternal journey
          </p>
        </div>
      </section>

      {/* Pregnancy Services */}
      <section className="relative min-h-screen flex items-center">
        <div className="w-full">
          <div className="grid lg:grid-cols-2 min-h-screen">
            {/* Image Side */}
            <div className="relative h-[600px] lg:h-auto">
              <img
                src="https://readdy.ai/api/search-image?query=radiant%20African%20American%20pregnant%20woman%20in%20comfortable%20home%20setting%20touching%20belly%20with%20gentle%20smile%20soft%20natural%20window%20lighting%20cozy%20nursery%20background%20peaceful%20maternal%20moment%20warm%20tones%20deep%20melanin%20skin%20tones%20pink%20accents&width=800&height=1000&seq=pregnancy-aa-v2-002&orientation=portrait"
                alt="Pregnancy Support"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-pink-900/20"></div>
            </div>

            {/* Content Side */}
            <div className="bg-gradient-to-br from-pink-50 to-white flex items-center">
              <div className="p-12 lg:p-16 space-y-8">
                <div>
                  <div className="inline-block bg-pink-500 text-white px-6 py-2 rounded-full font-semibold mb-4">
                    Pregnancy Support
                  </div>
                  <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                    Nurturing Your Pregnancy Journey
                  </h2>
                  <p className="text-lg text-gray-700 leading-relaxed mb-8">
                    From the moment you discover you're expecting, we're here to support you with comprehensive prenatal care, education, and emotional guidance.
                  </p>
                </div>

                <div className="space-y-4">
                  {[
                    'Prenatal visits and health monitoring',
                    'Nutritionist meal planning for optimal health',
                    'Birth plan development and preparation',
                    'Childbirth education and breathing techniques',
                    'Partner support and involvement guidance',
                    'Emotional support throughout pregnancy',
                    'OB/Midwife appointment accompaniment',
                    'Resource coordination and referrals'
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-checkbox-circle-fill text-2xl text-emerald-500"></i>
                      </div>
                      <span className="text-gray-700 text-lg">{item}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/booking"
                  className="inline-block bg-gradient-to-r from-pink-500 to-pink-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:from-pink-600 hover:to-pink-700 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
                >
                  Book Pregnancy Support
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Birth Services */}
      <section className="relative min-h-screen flex items-center">
        <div className="w-full">
          <div className="grid lg:grid-cols-2 min-h-screen">
            {/* Content Side - Left */}
            <div className="bg-gradient-to-br from-emerald-50 to-white flex items-center order-2 lg:order-1">
              <div className="p-12 lg:p-16 space-y-8">
                <div>
                  <div className="inline-block bg-emerald-500 text-white px-6 py-2 rounded-full font-semibold mb-4">
                    Birth Support
                  </div>
                  <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                    Empowering Your Birth Experience
                  </h2>
                  <p className="text-lg text-gray-700 leading-relaxed mb-8">
                    We provide continuous support during labor and delivery, ensuring you feel safe, informed, and empowered throughout your birth experience.
                  </p>
                </div>

                <div className="space-y-4">
                  {[
                    'Continuous labor support and coaching',
                    'Pain management techniques and comfort measures',
                    'Advocacy for your birth preferences',
                    'Partner guidance and support',
                    'Immediate postpartum assistance',
                    'Breastfeeding initiation support',
                    'Emotional support during labor',
                    'Photography and birth story documentation'
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-checkbox-circle-fill text-2xl text-pink-500"></i>
                      </div>
                      <span className="text-gray-700 text-lg">{item}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/booking"
                  className="inline-block bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:from-emerald-600 hover:to-emerald-700 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
                >
                  Book Birth Support
                </Link>
              </div>
            </div>

            {/* Image Side - Right */}
            <div className="relative h-[600px] lg:h-auto order-1 lg:order-2">
              <img
                src="https://readdy.ai/api/search-image?query=peaceful%20African%20American%20woman%20in%20hospital%20gown%20during%20labor%20with%20supportive%20doula%20holding%20hands%20calm%20serene%20birthing%20room%20soft%20lighting%20empowering%20birth%20moment%20warm%20compassionate%20care%20deep%20melanin%20skin%20tones%20pink%20green%20accents&width=800&height=1000&seq=birth-aa-v2-002&orientation=portrait"
                alt="Birth Support"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-l from-transparent to-emerald-900/20"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Postpartum Services */}
      <section className="relative min-h-screen flex items-center">
        <div className="w-full">
          <div className="grid lg:grid-cols-2 min-h-screen">
            {/* Image Side */}
            <div className="relative h-[600px] lg:h-auto">
              <img
                src="https://readdy.ai/api/search-image?query=joyful%20African%20American%20mother%20holding%20newborn%20baby%20in%20cozy%20home%20nursery%20soft%20natural%20lighting%20peaceful%20postpartum%20moment%20breastfeeding%20support%20warm%20maternal%20bond%20deep%20melanin%20skin%20tones%20pink%20green%20botanical%20accents%20serene%20atmosphere&width=800&height=1000&seq=postpartum-aa-v2-002&orientation=portrait"
                alt="Postpartum Support"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-pink-900/20"></div>
            </div>

            {/* Content Side */}
            <div className="bg-gradient-to-br from-pink-50 via-emerald-50 to-white flex items-center">
              <div className="p-12 lg:p-16 space-y-8">
                <div>
                  <div className="inline-block bg-gradient-to-r from-pink-500 to-emerald-500 text-white px-6 py-2 rounded-full font-semibold mb-4">
                    Postpartum Support
                  </div>
                  <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                    Caring for You After Birth
                  </h2>
                  <p className="text-lg text-gray-700 leading-relaxed mb-8">
                    The postpartum period is a time of adjustment and healing. We provide comprehensive support to help you navigate the early days of parenthood with confidence.
                  </p>
                </div>

                <div className="space-y-4">
                  {[
                    'Postpartum home visits and check-ins',
                    'Lactation support and breastfeeding guidance',
                    'Newborn care education and assistance',
                    'Emotional support and mental health screening',
                    'Perineal bath and healing care',
                    'Help with nursery setup and organization',
                    'Meal preparation and nutritional guidance',
                    'Partner and family adjustment support'
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-checkbox-circle-fill text-2xl text-emerald-500"></i>
                      </div>
                      <span className="text-gray-700 text-lg">{item}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/booking"
                  className="inline-block bg-gradient-to-r from-pink-500 via-emerald-500 to-emerald-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-xl transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
                >
                  Book Postpartum Support
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Virtual Services Banner */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-pink-100 via-emerald-100 to-pink-100"></div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center space-y-8">
            <div className="inline-block bg-white px-6 py-2 rounded-full shadow-lg">
              <span className="text-emerald-600 font-semibold">Virtual Services Available</span>
            </div>
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-gray-900">
              Support From Anywhere
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Can't meet in person? We offer comprehensive virtual doula services via Zoom, providing the same level of care and support from the comfort of your home.
            </p>
            <div className="flex flex-wrap justify-center gap-8 pt-8">
              {[
                { icon: 'ri-vidicon-fill', label: 'Video Consultations' },
                { icon: 'ri-chat-4-fill', label: '24/7 Text Support' },
                { icon: 'ri-phone-fill', label: 'Phone Coaching' }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 bg-white px-6 py-4 rounded-full shadow-lg">
                  <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-emerald-400 rounded-full flex items-center justify-center">
                    <i className={`${item.icon} text-xl text-white`}></i>
                  </div>
                  <span className="font-semibold text-gray-900">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=happy%20African%20American%20family%20with%20newborn%20baby%20at%20home%20mother%20father%20and%20baby%20together%20peaceful%20loving%20moment%20soft%20natural%20lighting%20warm%20family%20bond%20deep%20melanin%20skin%20tones%20pink%20flowers%20green%20plants%20in%20background&width=1920&height=600&seq=servicescta-aa-v2-002&orientation=landscape"
            alt="Join our family"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/85 to-pink-900/85"></div>
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center text-white space-y-6">
          <h2 className="font-playfair text-4xl lg:text-5xl font-bold">
            Let's Start Your Journey Together
          </h2>
          <p className="text-xl">
            Schedule your free consultation and discover which services are right for you.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-4">
            <Link
              to="/booking"
              className="inline-block bg-white text-pink-600 px-10 py-4 rounded-full font-semibold text-lg hover:bg-pink-50 transition-colors duration-300 shadow-lg whitespace-nowrap cursor-pointer"
            >
              Book Free Consultation
            </Link>
            <Link
              to="/packages"
              className="inline-block bg-transparent border-2 border-white text-white px-10 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-pink-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              View Packages
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;